import { useQueryClient } from "@tanstack/react-query";

import { useFetchMutation, useFetchQuery } from "common/queries/hooks";
import { FetchError } from "common/fetch/errors";
import { PaymentErrorData } from "common/types/payments";
import { WithStringValues } from "common/utils/types";

import {
  DetailSubscription,
  OnboardingDetailSubscription,
  SubscriptionUpdate,
  SubscriptionPaymentUpdate,
  BulkUpdateSubscriptions,
  BulkUpdateSubscriptionsResponse,
  ListSubscription,
  SubscriptionListSearch,
} from "../types";
import { keys, urls } from "./constants";
import { useInvalidateClient } from "../../accounts/hooks/invalidate";

const noop = () => undefined;

export const useSubscriptionsQuery = (
  search: WithStringValues<SubscriptionListSearch> = {},
  { enabled = true }: { enabled?: boolean } = {},
) => {
  return useFetchQuery<ListSubscription[], WithStringValues<SubscriptionListSearch>>(
    keys.subscriptionList(search),
    {
      fetch: { url: urls.subscriptions(), body: search },
      config: { keepPreviousData: true, enabled },
    },
  );
};

export const useSubscriptionQuery = (id: number) => {
  return useFetchQuery<DetailSubscription>(keys.subscriptionDetail(id), {
    fetch: { url: urls.subscription(id) },
  });
};

export const useOnboardingSubscriptionQuery = (id: number) => {
  return useFetchQuery<OnboardingDetailSubscription>(keys.onboardingSubscriptionDetail(id), {
    fetch: { url: urls.onboardingSubscription(id) },
  });
};

export const useUpdateSubscriptionMutation = (
  id: number,
  { onSuccess }: { onSuccess: () => void },
) => {
  const queryClient = useQueryClient();
  const invalidateClient = useInvalidateClient();
  return useFetchMutation<
    SubscriptionUpdate,
    DetailSubscription,
    DetailSubscription,
    SubscriptionUpdate,
    // PaymentErrorData only applicable for OrderNow actions
    FetchError<SubscriptionUpdate, PaymentErrorData>
  >({
    fetch: (body) => ({ method: "PATCH", url: urls.subscription(id), body }),
    config: {
      onSuccess: (data) => {
        queryClient.setQueryData(keys.subscriptionDetail(id), data);
        queryClient.invalidateQueries(keys.subscriptions());
        // client detail shows the active/paused subscriptions for each address
        invalidateClient();
        onSuccess();
      },
    },
  });
};

export const useUpdateSubscriptionPaymentMethodMutation = (
  id: number,
  {
    onSuccess = noop,
    onError = noop,
  }: {
    onSuccess?: (data: DetailSubscription) => void;
    onError?: (
      error: FetchError<SubscriptionPaymentUpdate, PaymentErrorData>,
      data: SubscriptionPaymentUpdate,
    ) => void;
  },
) => {
  const queryClient = useQueryClient();
  return useFetchMutation<
    SubscriptionPaymentUpdate,
    DetailSubscription,
    DetailSubscription,
    SubscriptionPaymentUpdate,
    FetchError<SubscriptionPaymentUpdate, PaymentErrorData>
  >({
    fetch: (body) => ({ method: "PATCH", url: urls.subscriptionPayment(id), body }),
    config: {
      onSuccess: (data) => {
        queryClient.setQueryData(keys.subscriptionDetail(id), data);
        queryClient.invalidateQueries(keys.subscriptions());
        onSuccess(data);
      },
      onError,
    },
  });
};

export const useUpdateBulkSubscriptionsMutation = ({
  onSuccess = noop,
  onError = noop,
}: {
  onSuccess?: () => void;
  onError?: (
    error: FetchError<BulkUpdateSubscriptions, PaymentErrorData>,
    data: BulkUpdateSubscriptions,
  ) => void;
} = {}) => {
  const queryClient = useQueryClient();
  const invalidateClient = useInvalidateClient();
  return useFetchMutation<
    BulkUpdateSubscriptions,
    BulkUpdateSubscriptionsResponse,
    void,
    BulkUpdateSubscriptions,
    FetchError<BulkUpdateSubscriptions, PaymentErrorData>
  >({
    fetch: (body) => ({ method: "POST", url: urls.bulkUpdateSubscriptions(), body }),
    config: {
      onSuccess: () => {
        queryClient.invalidateQueries(keys.subscriptions());
        invalidateClient();
        onSuccess();
      },
      onError,
    },
  });
};
